import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Briefcase,
  Users,
  MapPin,
  CalendarDays,
  Clock,
  Car,
  PencilLine,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import PlaceInput from "@/components/PlaceInput";
import { classifyTrip } from "@/lib/tripClassifier";
import { getPricedOptions } from "@/lib/pricing";
import { loadGoogleMapsCore } from "@/lib/googleMaps";
import { trackEvent } from "@/lib/analytics";
import BookingCheckoutDialog from "@/components/BookingCheckoutDialog";

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "+966540423401";

const DURATION_OPTIONS = Array.from({ length: 24 }, (_, i) => (i + 1) * 2);

function buildMapEmbedLink(trip) {
  if (trip.mode === "dropoff" && trip.from && trip.to) {
    return `https://www.google.com/maps?output=embed&saddr=${encodeURIComponent(trip.from)}&daddr=${encodeURIComponent(trip.to)}`;
  }

  if (trip.from) {
    return `https://www.google.com/maps?q=${encodeURIComponent(trip.from)}&output=embed`;
  }

  return "";
}

function formatEtaTime(dateValue, timeValue, durationSeconds) {
  if (!timeValue || !durationSeconds) return "";

  const baseDate = dateValue ? new Date(`${dateValue}T${timeValue}:00`) : new Date();
  if (Number.isNaN(baseDate.getTime())) return "";

  const eta = new Date(baseDate.getTime() + durationSeconds * 1000);
  return eta.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function estimateFallbackDurationSeconds({ tripType, originCity, destinationCity, isAirport }) {
  if (tripType === "jeddahMakkah") return 90 * 60;
  if (tripType === "makkahMadinah") return 4 * 60 * 60;

  if (isAirport) {
    if (destinationCity === "makkah" || originCity === "makkah") return 90 * 60;
    if (destinationCity === "madinah" || originCity === "madinah") return 4 * 60 * 60;
    if (destinationCity === "jeddah" || originCity === "jeddah") return 35 * 60;
    return 45 * 60;
  }

  if (originCity === destinationCity && originCity) return 35 * 60;
  if (originCity === "jeddah" && destinationCity === "madinah") return 4 * 60 * 60;
  if (originCity === "riyadh" || destinationCity === "riyadh") return 60 * 60;
  if (tripType === "general") return 60 * 60;
  return 0;
}

function truncateAddress(value, maxLength = 44) {
  if (!value) return "";
  const cleaned = String(value).replace(/\s+/g, " ").trim();
  if (cleaned.length <= maxLength) return cleaned;
  return `${cleaned.slice(0, Math.max(0, maxLength - 2)).trim()}..`;
}

export default function BookingOptions() {
  const { lang } = useLanguage();
  const isArabic = lang === "ar";
  const [searchParams] = useSearchParams();

  const fromRef = useRef(null);
  const toRef = useRef(null);

  const [trip, setTrip] = useState({
    mode: searchParams.get("mode") || "dropoff",
    from: searchParams.get("from") || "",
    to: searchParams.get("to") || "",
    date: searchParams.get("date") || "",
    time: searchParams.get("time") || "",
    duration: searchParams.get("duration") || "4",
    specialInstructions: "",
    pickupPlace: {
      placeId: searchParams.get("pickupPlaceId") || "",
      name: searchParams.get("pickupPlaceName") || "",
      address: searchParams.get("from") || "",
      types: (searchParams.get("pickupPlaceType") || "").split(",").filter(Boolean),
      lat: searchParams.get("pickupLat") || "",
      lng: searchParams.get("pickupLng") || "",
    },
    dropoffPlace: {
      placeId: searchParams.get("dropoffPlaceId") || "",
      name: searchParams.get("dropoffPlaceName") || "",
      address: searchParams.get("to") || "",
      types: (searchParams.get("dropoffPlaceType") || "").split(",").filter(Boolean),
      lat: searchParams.get("dropoffLat") || "",
      lng: searchParams.get("dropoffLng") || "",
    },
  });

  const [selectedId, setSelectedId] = useState("business");
  const [editing, setEditing] = useState(false);
  const [expandedOptionId, setExpandedOptionId] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [routeMeta, setRouteMeta] = useState({ durationText: "", durationSeconds: 0 });

  const classification = useMemo(() => {
    return classifyTrip({
      mode: trip.mode,
      pickupPlaceName: trip.pickupPlace?.name,
      pickupAddress: trip.from,
      pickupPlaceTypes: trip.pickupPlace?.types,
      dropoffPlaceName: trip.dropoffPlace?.name,
      dropoffAddress: trip.to,
      dropoffPlaceTypes: trip.dropoffPlace?.types,
    });
  }, [trip]);

  const pricedOptions = useMemo(() => {
    return getPricedOptions({
      tripType: classification.tripType,
      mode: trip.mode,
      duration: trip.duration,
    });
  }, [classification.tripType, trip.mode, trip.duration]);

  const selected = pricedOptions.find((item) => item.id === selectedId) || pricedOptions[0];

  const serviceLabel = useMemo(() => {
    if (trip.mode === "hourly") {
      return isArabic ? `حجز بالساعات - ${trip.duration} ساعات` : `Hourly Booking - ${trip.duration} hours`;
    }
    return classification.serviceLabel;
  }, [trip.mode, trip.duration, classification.serviceLabel, isArabic]);

  const mapEmbedUrl = useMemo(() => buildMapEmbedLink(trip), [trip]);

  const desktopTripSummary = useMemo(() => {
    const fromLabel = truncateAddress(trip.from, trip.mode === "dropoff" ? 40 : 78);
    if (trip.mode !== "dropoff") return fromLabel;
    const toLabel = truncateAddress(trip.to, 34);
    return `${fromLabel} → ${toLabel}`;
  }, [trip.from, trip.to, trip.mode]);

  const mapPickupLabel = useMemo(() => truncateAddress(trip.from, 42), [trip.from]);
  const mapDropoffLabel = useMemo(() => truncateAddress(trip.to, 38), [trip.to]);
  const fallbackEtaText = useMemo(() => {
    const fallbackSeconds = estimateFallbackDurationSeconds({
      tripType: classification.tripType,
      originCity: classification.originCity,
      destinationCity: classification.destinationCity,
      isAirport: classification.isAirport,
    });
    return formatEtaTime(trip.date, trip.time, fallbackSeconds);
  }, [classification.tripType, classification.originCity, classification.destinationCity, classification.isAirport, trip.date, trip.time]);

  const displayEtaText = useMemo(() => {
    if (trip.mode !== "dropoff") return "";
    if (routeMeta.durationSeconds) {
      return formatEtaTime(trip.date, trip.time, routeMeta.durationSeconds);
    }
    return fallbackEtaText;
  }, [trip.mode, trip.date, trip.time, routeMeta.durationSeconds, fallbackEtaText]);

  useEffect(() => {
    let cancelled = false;

    async function computeRouteMeta() {
      if (!cancelled) {
        setRouteMeta({ durationText: "", durationSeconds: 0 });
      }

      if (trip.mode !== "dropoff" || !trip.from || !trip.to) {
        return;
      }

      try {
        await loadGoogleMapsCore();
        if (cancelled || !window.google?.maps?.DistanceMatrixService) return;

        const distanceMatrixService = new window.google.maps.DistanceMatrixService();
        const origin = trip.pickupPlace?.lat && trip.pickupPlace?.lng
          ? { lat: Number(trip.pickupPlace.lat), lng: Number(trip.pickupPlace.lng) }
          : trip.from;
        const destination = trip.dropoffPlace?.lat && trip.dropoffPlace?.lng
          ? { lat: Number(trip.dropoffPlace.lat), lng: Number(trip.dropoffPlace.lng) }
          : trip.to;

        distanceMatrixService.getDistanceMatrix(
          {
            origins: [origin],
            destinations: [destination],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.METRIC,
          },
          (response, status) => {
            if (cancelled || status !== "OK") {
              if (!cancelled) {
                setRouteMeta({ durationText: "", durationSeconds: 0 });
              }
              return;
            }

            const element = response?.rows?.[0]?.elements?.[0];
            const durationText = element?.duration?.text || "";
            const durationSeconds = element?.duration?.value || 0;
            setRouteMeta({ durationText, durationSeconds });
          }
        );
      } catch (error) {
        if (!cancelled) {
          console.error("Distance matrix ETA error:", error);
          setRouteMeta({ durationText: "", durationSeconds: 0 });
        }
      }
    }

    computeRouteMeta();

    return () => {
      cancelled = true;
    };
  }, [trip.mode, trip.from, trip.to, trip.date, trip.time, trip.pickupPlace?.lat, trip.pickupPlace?.lng, trip.dropoffPlace?.lat, trip.dropoffPlace?.lng]);

  useEffect(() => {
    trackEvent("booking_options_view", {
      booking_mode: trip.mode,
      trip_type: classification.tripType,
      service_label: serviceLabel,
      date: trip.date,
      time: trip.time,
      duration: trip.mode === "hourly" ? trip.duration : "",
    });
  }, [trip.mode, classification.tripType, serviceLabel, trip.date, trip.time, trip.duration]);

  const updateTripFromWidgetValues = () => {
    const liveFrom = fromRef.current?.getValue?.() || trip.from;
    const liveTo = toRef.current?.getValue?.() || trip.to;

    setTrip((prev) => ({
      ...prev,
      from: liveFrom.trim(),
      to: prev.mode === "dropoff" ? liveTo.trim() : "",
    }));
  };

  const mobilePriceLabel =
    selected.pricingType === "quote"
      ? isArabic
        ? "عرض سعر"
        : "Request quote"
      : `SAR ${selected.price}`;

  return (
    <div dir={isArabic ? "rtl" : "ltr"} className="min-h-screen bg-[#050505] text-white booking-options-page">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0b0b0b]/95 backdrop-blur booking-options-header">
        <div className="max-w-7xl mx-auto px-4 md:px-10 py-4 md:py-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between booking-options-header-inner">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="inline-flex items-center shrink-0">
              <img src="/logo-white.png" alt="BlackRide Logo" className="h-8 md:h-10 w-auto object-contain" />
            </Link>

            <button
              onClick={() => setEditing((prev) => !prev)}
              className="lg:hidden inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#111] px-4 py-2.5 text-sm font-medium"
            >
              <PencilLine className="w-4 h-4 text-[#C7A239]" />
              {editing ? (isArabic ? "إغلاق" : "Close") : (isArabic ? "تعديل" : "Edit")}
            </button>
          </div>

          <div className="hidden lg:grid lg:grid-cols-[220px_minmax(360px,1fr)_250px] gap-3 items-center flex-1 min-w-0">
            <div className="rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-sm min-w-0">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Car className="w-4 h-4" />
                <span>{isArabic ? "الخدمة" : "Service"}</span>
              </div>
              <div className="font-medium text-white">{serviceLabel}</div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-sm min-w-0">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <MapPin className="w-4 h-4" />
                <span>{isArabic ? "الرحلة" : "Trip"}</span>
              </div>
              <div className="font-medium text-white truncate" title={trip.mode === "dropoff" ? `${trip.from} → ${trip.to}` : trip.from}>
                {desktopTripSummary}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-sm min-w-0">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <CalendarDays className="w-4 h-4" />
                <span>{isArabic ? "الموعد" : "Schedule"}</span>
              </div>
              <div className="font-medium text-white">
                {trip.date} <span className="text-gray-500 mx-1">•</span> {trip.time}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-[1fr_1fr] gap-2 lg:hidden">
            <div className="rounded-2xl border border-white/10 bg-[#111] px-3 py-3 text-xs">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Car className="w-3.5 h-3.5 text-[#C7A239]" />
                <span>{isArabic ? "الخدمة" : "Service"}</span>
              </div>
              <div className="font-medium text-white truncate">{serviceLabel}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#111] px-3 py-3 text-xs">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <CalendarDays className="w-3.5 h-3.5 text-[#C7A239]" />
                <span>{isArabic ? "الموعد" : "Schedule"}</span>
              </div>
              <div className="font-medium text-white truncate">{trip.date} • {trip.time}</div>
            </div>
            <div className="col-span-2 rounded-2xl border border-white/10 bg-[#111] px-3 py-3 text-xs">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <MapPin className="w-3.5 h-3.5 text-[#C7A239]" />
                <span>{isArabic ? "الرحلة" : "Trip"}</span>
              </div>
              <div className="font-medium text-white truncate">
                {trip.from}
                {trip.mode === "dropoff" && (
                  <>
                    <span className="text-gray-500"> → </span>
                    {trip.to}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-10 py-5 md:py-10 pb-36 lg:pb-10">
        <div className="grid grid-cols-1 xl:grid-cols-[1.75fr_0.95fr] gap-8">
          <section className="min-w-0">
            <div className="flex items-center justify-between gap-4 mb-6 md:mb-8">
              <h1 className="font-['Playfair_Display'] text-3xl md:text-5xl text-white">
                {isArabic ? "اختر تجربتك" : "Choose your experience"}
              </h1>

              <button
                onClick={() => setEditing((prev) => !prev)}
                className="hidden lg:inline-flex border border-white/10 hover:border-white/20 px-5 py-3 rounded-full text-sm"
              >
                {editing ? (isArabic ? "إغلاق التعديل" : "Close edit") : (isArabic ? "تعديل الرحلة" : "Edit trip")}
              </button>
            </div>

            {editing && (
              <div className="rounded-[28px] bg-[#111] border border-white/10 p-5 md:p-6 mb-6 md:mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">{isArabic ? "نوع الحجز" : "Booking mode"}</label>
                  <select
                    value={trip.mode}
                    onChange={(e) => setTrip((prev) => ({ ...prev, mode: e.target.value }))}
                    className="w-full rounded-2xl bg-[#0b0b0b] border border-white/10 px-4 py-3 text-white"
                  >
                    <option value="dropoff">Drop off</option>
                    <option value="hourly">Hourly Booking</option>
                  </select>
                </div>

                {trip.mode === "hourly" && (
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">{isArabic ? "المدة" : "Duration"}</label>
                    <select
                      value={trip.duration}
                      onChange={(e) => setTrip((prev) => ({ ...prev, duration: e.target.value }))}
                      className="w-full rounded-2xl bg-[#0b0b0b] border border-white/10 px-4 py-3 text-white"
                    >
                      {DURATION_OPTIONS.map((hours) => (
                        <option key={hours} value={String(hours)}>
                          {hours} {isArabic ? "ساعات" : "hours"}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm text-gray-400 mb-2">{isArabic ? "من" : "From"}</label>
                  <PlaceInput
                    ref={fromRef}
                    value={trip.from}
                    onChange={(value) => setTrip((prev) => ({ ...prev, from: value }))}
                    onPlaceSelect={(place) =>
                      setTrip((prev) => ({
                        ...prev,
                        from: place.address,
                        pickupPlace: place,
                      }))
                    }
                    placeholder={isArabic ? "من أين؟" : "From"}
                    className="place-input-shell place-input-dark rounded-2xl bg-[#0b0b0b] border border-white/10 px-4 py-3"
                  />
                </div>

                {trip.mode === "dropoff" && (
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">{isArabic ? "إلى" : "To"}</label>
                    <PlaceInput
                      ref={toRef}
                      value={trip.to}
                      onChange={(value) => setTrip((prev) => ({ ...prev, to: value }))}
                      onPlaceSelect={(place) =>
                        setTrip((prev) => ({
                          ...prev,
                          to: place.address,
                          dropoffPlace: place,
                        }))
                      }
                      placeholder={isArabic ? "إلى أين؟" : "To"}
                      className="place-input-shell place-input-dark rounded-2xl bg-[#0b0b0b] border border-white/10 px-4 py-3"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm text-gray-400 mb-2">{isArabic ? "التاريخ" : "Date"}</label>
                  <input
                    type="date"
                    value={trip.date}
                    onChange={(e) => setTrip((prev) => ({ ...prev, date: e.target.value }))}
                    className="w-full rounded-2xl bg-[#0b0b0b] border border-white/10 px-4 py-3 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">{isArabic ? "الوقت" : "Time"}</label>
                  <input
                    type="time"
                    value={trip.time}
                    onChange={(e) => setTrip((prev) => ({ ...prev, time: e.target.value }))}
                    className="w-full rounded-2xl bg-[#0b0b0b] border border-white/10 px-4 py-3 text-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-400 mb-2">{isArabic ? "ملاحظات خاصة" : "Special instructions"}</label>
                  <textarea
                    value={trip.specialInstructions}
                    onChange={(e) => setTrip((prev) => ({ ...prev, specialInstructions: e.target.value }))}
                    rows={4}
                    placeholder={isArabic ? "أي تعليمات إضافية للسائق" : "Any instructions for the chauffeur"}
                    className="w-full rounded-2xl bg-[#0b0b0b] border border-white/10 px-4 py-3 text-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <button
                    onClick={() => {
                      updateTripFromWidgetValues();
                      trackEvent("trip_update", {
                        booking_mode: trip.mode,
                        trip_type: classification.tripType,
                        from: trip.from,
                        to: trip.mode === "dropoff" ? trip.to : "",
                        date: trip.date,
                        time: trip.time,
                        duration: trip.mode === "hourly" ? trip.duration : "",
                      });
                      setEditing(false);
                    }}
                    className="bg-[#C7A239] hover:bg-[#D4B34A] text-black font-medium px-6 py-3 rounded-full"
                  >
                    {isArabic ? "تحديث الرحلة" : "Update trip"}
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4 lg:space-y-6 max-h-none xl:max-h-[calc(100vh-220px)] overflow-visible xl:overflow-y-auto pr-0 xl:pr-1">
              {pricedOptions.map((option) => {
                const isSelected = option.id === selectedId;
                const isExpanded = expandedOptionId === option.id;

                return (
                  <button
                    key={option.id}
                    onClick={() => {
                      setSelectedId(option.id);
                      trackEvent("booking_option_select", {
                        vehicle_id: option.id,
                        vehicle_name: option.name,
                        vehicle_model: option.vehicle,
                        booking_mode: trip.mode,
                        trip_type: classification.tripType,
                        pricing_type: option.pricingType,
                        price: option.price ?? "",
                      });
                    }}
                    className={`w-full text-left rounded-[28px] bg-[#111] border p-4 md:p-6 transition-all shadow-sm ${
                      isSelected
                        ? "border-[#C7A239] ring-2 ring-[#C7A239]/25"
                        : "border-white/10 hover:border-white/20"
                    }`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-5 md:gap-6 items-center">
                      <div className="relative h-[190px] md:h-[210px] overflow-hidden rounded-[24px] bg-[radial-gradient(circle_at_top,#2a2a2a,#0b0b0b_68%)] p-4 md:p-5 shadow-[0_18px_40px_rgba(0,0,0,0.16)]">
                        <div className="absolute inset-x-10 bottom-5 h-9 rounded-full bg-black/55 blur-2xl opacity-80" />
                        <div className="relative h-full w-full overflow-hidden rounded-[18px] border border-white/8 bg-[#0d0d0d] shadow-[0_18px_34px_rgba(0,0,0,0.28)]">
                          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),transparent_22%,rgba(0,0,0,0.18))] z-10 pointer-events-none" />
                          <img
                            src={option.image}
                            alt={option.vehicle}
                            className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <h2 className="text-2xl md:text-3xl font-semibold text-white">{option.name}</h2>
                            <p className="text-gray-400 text-base md:text-lg">{option.vehicle}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs md:text-sm text-gray-500">
                              {option.pricingType === "quote"
                                ? isArabic
                                  ? "عرض سعر"
                                  : "Request quote"
                                : "SAR"}
                            </p>
                            <p className="text-3xl md:text-4xl font-semibold text-white">
                              {option.pricingType === "quote" ? "—" : option.price}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-gray-400 mb-5">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            <span>{option.passengers}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            <span>{option.luggage}</span>
                          </div>
                          {trip.mode === "hourly" && (
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>
                                {isArabic ? "يتم احتساب" : "Charged for"} {option.billableHours} {isArabic ? "ساعات" : "hours"}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="border-t border-white/10 pt-4">
                          <div className="flex items-center justify-between gap-4">
                            <p className="text-sm uppercase tracking-wide text-gray-500">
                              {isArabic ? "يشمل هذا الخيار" : "Included in this class"}
                            </p>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setExpandedOptionId((current) => (current === option.id ? null : option.id));
                              }}
                              className="inline-flex items-center gap-2 text-sm text-[#C7A239] hover:text-[#D4B34A]"
                            >
                              <span>{isArabic ? (isExpanded ? "إخفاء" : "اعرف المزيد") : isExpanded ? "Show less" : "Know more"}</span>
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>

                          <div
                            className={`grid transition-all duration-300 ease-out ${isExpanded ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0 mt-0"}`}
                          >
                            <div className="overflow-hidden">
                              <ul className="space-y-2 text-sm text-gray-300">
                                {option.details.map((detail) => (
                                  <li key={detail}>• {detail}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <aside className="hidden xl:block space-y-6 xl:sticky xl:top-[120px] self-start">
            <div className="rounded-[28px] overflow-hidden bg-[#111] border border-white/10 shadow-sm booking-desktop-map-card">
              <div className="relative aspect-[4/3] bg-[linear-gradient(135deg,#16181f,#232734)] booking-desktop-map-surface">
                {mapEmbedUrl ? (
                  <iframe
                    title="Trip route map"
                    src={mapEmbedUrl}
                    className="absolute inset-0 h-full w-full booking-desktop-map-frame"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : null}
                <div className="absolute inset-0 booking-desktop-map-overlay" />


                <div className="absolute top-5 left-5 w-[44%] min-w-[220px] max-w-[320px] rounded-xl bg-white/96 shadow-xl text-[#111] overflow-hidden booking-map-callout booking-map-callout-pickup">
                  <div className="flex items-stretch">
                    <div className="px-4 py-3 min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500 mb-1">
                        {isArabic ? "الانطلاق" : "Pick up"}
                      </p>
                      <p className="font-medium text-[13px] leading-5 truncate">{mapPickupLabel || (isArabic ? "غير محدد" : "Not set")}</p>
                    </div>
                    <div className="bg-[#2167c7] px-3 py-3 text-white flex flex-col items-center justify-center min-w-[84px] booking-map-time-pill">
                      <span className="text-[10px] uppercase tracking-[0.16em] opacity-80">{isArabic ? "الوقت" : "Time"}</span>
                      <span className="text-[15px] font-semibold mt-1">{trip.time || "--:--"}</span>
                    </div>
                  </div>
                </div>

                {trip.mode === "dropoff" && (
                  <div className="absolute right-5 bottom-5 w-[42%] min-w-[220px] max-w-[300px] rounded-xl bg-white/96 shadow-xl text-[#111] overflow-hidden booking-map-callout booking-map-callout-dropoff">
                    <div className="flex items-stretch">
                      <div className="px-4 py-3 min-w-0 flex-1">
                        <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500 mb-1">
                          {isArabic ? "الوصول" : "Drop-off"}
                        </p>
                        <p className="font-medium text-[13px] leading-5 truncate">{mapDropoffLabel || (isArabic ? "غير محدد" : "Not set")}</p>
                      </div>
                      <div className="bg-[#131722] px-3 py-3 text-white flex flex-col items-center justify-center min-w-[84px] booking-map-time-pill">
                        <span className="text-[10px] uppercase tracking-[0.16em] opacity-80">{trip.mode === "dropoff" ? (isArabic ? "الوصول المتوقع" : "ETA") : (isArabic ? "الخدمة" : "Trip")}</span>
                        <span className="text-[13px] font-semibold mt-1 text-center">{trip.mode === "dropoff" ? (displayEtaText || "--:--") : (isArabic ? "حسب الساعة" : "Hourly")}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-[28px] bg-[#111] border border-white/10 shadow-sm p-6">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h3 className="text-3xl font-semibold text-white">{selected.name}</h3>
                  <p className="text-gray-400">{selected.vehicle}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">
                    {selected.pricingType === "quote"
                      ? isArabic
                        ? "عرض سعر"
                        : "Request quote"
                      : "SAR"}
                  </p>
                  <p className="text-4xl font-semibold text-white">
                    {selected.pricingType === "quote" ? "—" : selected.price}
                  </p>
                </div>
              </div>

              <div className="space-y-3 text-sm text-gray-400 border-t border-white/10 pt-5 mb-6">
                <div className="flex items-center justify-between gap-4">
                  <span>{isArabic ? "الخدمة" : "Service"}</span>
                  <span className="font-medium text-white text-right">{serviceLabel}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span>{isArabic ? "من" : "From"}</span>
                  <span className="font-medium text-white text-right">{trip.from}</span>
                </div>
                {trip.mode === "dropoff" && (
                  <div className="flex items-center justify-between gap-4">
                    <span>{isArabic ? "إلى" : "To"}</span>
                    <span className="font-medium text-white text-right">{trip.to}</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-4">
                  <span>{isArabic ? "التاريخ" : "Date"}</span>
                  <span className="font-medium text-white">{trip.date}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span>{isArabic ? "الوقت" : "Time"}</span>
                  <span className="font-medium text-white">{trip.time}</span>
                </div>
                {trip.mode === "hourly" && (
                  <div className="flex items-center justify-between gap-4">
                    <span>{isArabic ? "المدة" : "Duration"}</span>
                    <span className="font-medium text-white">{trip.duration} {isArabic ? "ساعات" : "hours"}</span>
                  </div>
                )}
                {trip.specialInstructions && (
                  <div className="flex items-start justify-between gap-4">
                    <span>{isArabic ? "ملاحظات" : "Notes"}</span>
                    <span className="font-medium text-white text-right max-w-[60%]">
                      {trip.specialInstructions}
                    </span>
                  </div>
                )}
              </div>

              {selected.pricingType === "quote" && (
                <div className="rounded-2xl bg-[#0b0b0b] border border-white/10 px-4 py-3 text-sm text-gray-300 mb-4">
                  {isArabic
                    ? "هذا المسار يحتاج تسعير مخصص. اضغط متابعة لطلب عرض سعر."
                    : "This route needs a custom quote. Continue to request pricing."}
                </div>
              )}

              <button
                type="button"
                onClick={() => setCheckoutOpen(true)}
                className="block w-full text-center bg-[#C7A239] hover:bg-[#D4B34A] text-black font-medium px-6 py-4 rounded-full transition"
              >
                {selected.pricingType === "quote"
                  ? (isArabic ? "طلب عرض سعر" : "Request quote")
                  : (isArabic ? `Book ${selected.name}` : `Book ${selected.name}`)}
              </button>

              <button
                onClick={() => setEditing(true)}
                className="block w-full text-center mt-3 border border-white/10 hover:border-white/20 text-white font-medium px-6 py-4 rounded-full transition"
              >
                {isArabic ? "تعديل الرحلة" : "Back to edit trip"}
              </button>
            </div>
          </aside>
        </div>
      </main>

      <BookingCheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        trip={trip}
        selected={selected}
        classification={classification}
        routeDurationText={routeMeta.durationText}
      />

      {!editing && (
        <div className="xl:hidden fixed inset-x-4 bottom-4 z-50 booking-mobile-selection-bar">
          <div className="rounded-[28px] border border-white/10 bg-[#0b0b0b]/95 backdrop-blur-xl p-4 shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-1">{isArabic ? "الفئة المختارة" : "Selected option"}</p>
                <p className="text-lg font-semibold text-white truncate">{selected.name}</p>
                <p className="text-sm text-gray-400 truncate">{selected.vehicle}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-1">{selected.pricingType === "quote" ? (isArabic ? "السعر" : "Pricing") : "SAR"}</p>
                <p className="text-xl font-semibold text-white">{mobilePriceLabel}</p>
              </div>
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-3">
              <button
                type="button"
                onClick={() => setCheckoutOpen(true)}
                className="inline-flex items-center justify-center rounded-full bg-[#C7A239] hover:bg-[#D4B34A] text-black font-medium px-5 py-4 transition"
              >
                {selected.pricingType === "quote"
                  ? (isArabic ? "طلب عرض سعر" : "Request quote")
                  : (isArabic ? "احجز الآن" : "Book now")}
              </button>
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.04] px-4 py-4 text-white"
                aria-label={isArabic ? "تعديل الرحلة" : "Edit trip"}
              >
                <PencilLine className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
